import { isWhiteLabel } from "@/branding";
import { goSolidApi } from "@/composables/useGoSolidApi";
import { useHelpCrunch } from "@/composables/useHelpCrunch";
import { applyAccountLanguage, resolveLocale } from "@/i18n";
import type {
  Account,
  AccountUpdateResult,
  Company,
  ManagingAccountResponse,
} from "@/types";
import { apiErrorLogFields } from "@/utils/apiErrors";
import { useLocalStorage } from "@vueuse/core";
import { defineStore } from "pinia";
import { computed, reactive, readonly, watch, type WatchHandle } from "vue";
import type {
  RouteLocation,
  RouteLocationNormalized,
  Router,
} from "vue-router";

/**
 * The logged-in account (`GET /api/account`), shared by every guard and component.
 *
 * Loads share one cached request until something may have changed the account: arriving on another
 * page (`markAccountStaleOnPageChange`), an account switch, or a change the store took. Each page's
 * first load therefore refetches it; until that lands, readers show the account the previous page left.
 *
 * Every answer that carries the account tree goes into the store — posted through `postAccountChange`
 * and the actions built on it, or handed to `applyAccountUpdate` by a caller that posted it itself —
 * so it reaches every reader, not only the component that sent the request.
 *
 * `account` is one object for the app's lifetime, updated in place, so whatever took a reference at
 * setup — `useCompanyFinder`, `useAccountFeatureFlags`, a child's prop — keeps reading the current
 * account. It is the server's copy: a form edits local refs or a copy and posts that.
 */
export const useAccountStore = defineStore("account", () => {
  const account = reactive<Account>(emptyAccount());

  // Bumped whenever a read starts and whenever the cached one is forgotten. An answer sent at an
  // earlier version may predate a change the store has seen since, so it is not taken as current.
  let version = 0;
  // The cached GET, in flight or answered.
  let request: Promise<Readonly<Account>> | null = null;

  const isManagingAccount = computed(() => account.managing_account != null);

  /**
   * Off, the account reads as the client's own: the fields every admin-only surface renders behind
   * are nulled, so the page shows what the client sees. Presentation only — the server still answers
   * as the admin. Kept per browser; the top bar's switch sets it while managing a client, and it
   * turns back on whenever the session stops managing one, whichever path stopped it.
   */
  const showAdminFeatures = useLocalStorage("show_admin_features", true);
  let serverAdminFeatures: AdminFeatureFields = adminFeatureFields(account);

  function takeAccount(data: Account): void {
    if ("managing_account" in data && data.managing_account == null) {
      showAdminFeatures.value = true;
    }
    serverAdminFeatures = adminFeatureFields(data);
    Object.assign(account, data, presentedAdminFeatures());
  }

  function presentedAdminFeatures(): AdminFeatureFields {
    return showAdminFeatures.value
      ? serverAdminFeatures
      : { admin_account_id: null, admin_internal_role: null, trackings: null };
  }

  watch(showAdminFeatures, () =>
    Object.assign(account, presentedAdminFeatures())
  );

  /**
   * Resolves with the account, fetching it only when nothing is cached. Rejects when that request
   * fails, leaving readers on the account they had; the next call retries.
   */
  function load(): Promise<Readonly<Account>> {
    if (!request) request = fetchAccount(++version);
    return request;
  }

  /**
   * The next `load` refetches, and an answer still in flight is dropped. Readers keep the account
   * they have until then.
   */
  function markStale(): void {
    version++;
    request = null;
  }

  /** Refetches the account, for a change the server made without answering with the tree. */
  function reload(): Promise<Readonly<Account>> {
    markStale();
    return load();
  }

  /**
   * An admin started or stopped managing an account (`@/admin/manageAccount`). The server answers as
   * the new account from now on, in every tab, so whom the session manages shows at once (the top bar
   * reads it). The rest of `account` describes the previous account until the next `load` refetches it.
   */
  function applyManagingAccount(managing: ManagingAccountResponse): void {
    if (managing.managing_account == null) showAdminFeatures.value = true;
    account.managing_account = managing.managing_account;
    account.managing_account_id = managing.managing_account_id;
    markStale();
  }

  /**
   * Takes the account tree an endpoint answered a change with — unless it describes another account
   * than the one held: a save that outlived an account switch (`applyManagingAccount`). Whom the
   * session manages survives it: only `GET /api/account` reports that.
   */
  function applyAccountUpdate(updated: Account): void {
    if (account.id !== undefined && updated.id !== account.id) return;
    takeAccount(updated);
    markStale();
  }

  /** Posts to an endpoint that answers with the account tree, and takes the answer. */
  async function postAccountChange(
    url: string,
    body?: unknown
  ): Promise<AccountUpdateResult> {
    const sentAt = version;
    const { data } = await goSolidApi.post<AccountUpdateResult>(url, body);
    const overlapped = sentAt !== version;
    applyAccountUpdate(data.accountData);
    // A read or another change during the POST may be newer than this answer, which just replaced
    // it, and its page won't load again: re-read. The change is committed either way, so a failed
    // re-read only leaves readers on its answer until the next load retries.
    if (overlapped) {
      await load().catch((error: unknown) =>
        console.error(
          "Account refresh after a change failed",
          apiErrorLogFields(error)
        )
      );
    }
    return data;
  }

  /** Posts an edited copy of the account, the body every account-update endpoint takes (`AccountUpdateRequest`). */
  function postAccountData(
    url: string,
    accountData: Account,
    companyId?: number
  ): Promise<AccountUpdateResult> {
    return postAccountChange(url, { accountData, company_id: companyId });
  }

  /** Saves one of the account's companies; readers see the edit once the server has taken it. */
  function updateCompany(company: Company): Promise<AccountUpdateResult> {
    return postAccountData(
      `/api/company/${company.id}/update`,
      { ...account, companies: [company] },
      company.id
    );
  }

  async function fetchAccount(sentAt: number): Promise<Readonly<Account>> {
    const answer = await goSolidApi.get<Account>("/api/account").then(
      ({ data }) => ({ data }),
      (error: unknown) => ({ error })
    );
    // Forgotten meanwhile: whoever awaits it gets the account as `load` has it now.
    if (sentAt !== version) return load();
    if ("error" in answer) {
      request = null;
      throw answer.error;
    }
    if (!answer.data) {
      request = null;
      throw new Error("Failed to fetch account data");
    }
    takeAccount(answer.data);
    applyAccountToSession(account);
    return sharedAccount;
  }

  // Read-only all the way down at runtime, but typed shallowly: a `DeepReadonly` account, with its
  // readonly arrays, would not pass where readers take an `Account`.
  const sharedAccount = readonly(account) as Readonly<Account>;

  return {
    account: sharedAccount,
    isManagingAccount,
    showAdminFeatures,
    load,
    reload,
    markStale,
    applyManagingAccount,
    applyAccountUpdate,
    postAccountChange,
    postAccountData,
    updateCompany,
  };
});

// The navigation the hook last saw, so a guard's redirect counts as part of it.
let judgedNavigation: RouteLocation | undefined;

/**
 * `router.beforeEach` hook: arriving on another path forgets the account, so the new page's first load
 * refetches it and its guards and components share that request. A query-only change, such as the
 * dashboard picking a company, stays on the page.
 *
 * A guard's redirect continues the navigation it came from — vue-router passes that first attempt as
 * `redirectedFrom` — so the page it lands on keeps the account the guard loaded. So does leaving a
 * route whose meta sets `passesAccountOn` (Root), which loads the account only to pick the next page.
 */
export function markAccountStaleOnPageChange(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized
): void {
  const navigation = to.redirectedFrom ?? to;
  if (navigation === judgedNavigation) return;
  judgedNavigation = navigation;
  if (to.path !== from.path && !from.meta.passesAccountOn) {
    useAccountStore().markStale();
  }
}

/**
 * Keeps the interface language on the account's, unless `?language=` in the URL overrides it
 * (`resolveLocale`). Follows both: a guard can load the account before the URL changes, and a
 * query-only navigation can add or drop the override without a load.
 */
export function followAccountLanguage(router: Router): WatchHandle {
  const accountStore = useAccountStore();
  return watch(
    [
      () => router.currentRoute.value.query.language,
      () => accountStore.account.language,
    ],
    ([, language]) => applyAccountLanguage(language),
    { immediate: true, flush: "sync" }
  );
}

/** The fields the backend sets only for an admin-driven request; what `showAdminFeatures` off hides. */
type AdminFeatureFields = Pick<
  Account,
  "admin_account_id" | "admin_internal_role" | "trackings"
>;

function adminFeatureFields(source: Account): AdminFeatureFields {
  return {
    admin_account_id: source.admin_account_id,
    admin_internal_role: source.admin_internal_role,
    trackings: source.trackings,
  };
}

/** The account before the server has answered: what every field falls back to. */
export function emptyAccount(): Account {
  return {
    email: null,
    name: null,
    registry_code: null,
    phone_number: null,
    has_registered: false,
    session_id: null,
    kilowatts: 200.0,
    go_price: 4.0,
    companies: [],
    trackings: [],
  };
}

/** The support chat follows the account the server says is logged in. */
function applyAccountToSession(data: Account): void {
  // SECURITY: HelpCrunch is Soldera's support tool; never forward account data to it from a
  // white-label host.
  if (isWhiteLabel()) return;
  try {
    useHelpCrunch().setHelpCrunchUser({
      email: data.email,
      name: data.name || data.email,
      user_id: data.id?.toString(),
      phone: data.phone_number,
      locale: resolveLocale(data.language),
      company: data.companies.map((company) => company.name).join(", "),
    });
  } catch (error) {
    // Support must never break the app.
    console.error("Error updating HelpCrunch user:", error);
  }
}
